import { Suspense, useRef } from 'react';
import { Button } from '#components/atoms/buttons/Button.tsx';
import type { ConfigKey } from '#components/patterns/config-overrides/labels/ConfigOverrideLabels.ts';
import { ConfirmButtonGroup } from '#components/patterns/confirmations/confirm-button-group/ConfirmButtonGroup.tsx';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '#components/patterns/modal/index.ts';
import type { ConfigOverride } from '#dn-types/host.ts';
import { mapOverridesToForm } from '#screens/hosts/form/advanced/mappers/mapOverridesToForm.ts';
import { LoadingSpinner } from '#screens/loading/LoadingSpinner.tsx';
import { EditConfigOverrideFormSwitch } from '#screens/tags/form/config-overrides/EditConfigOverrideFormSwitch.tsx';

type Props = {
  onCancel: () => void;
  onSave: (values: ConfigOverride[]) => void;
  configType: ConfigKey;
  value: ConfigOverride;
  /** Need all of these so we can get all configured stats */
  existingOverrides: ConfigOverride[];
};

export function EditConfigOverrideModal({ onSave, onCancel, configType, value, existingOverrides }: Props) {
  const modalRef = useRef(null);

  const stats = mapOverridesToForm(existingOverrides).stats;
  const remoteCIDRs = existingOverrides.flatMap((o) =>
    o.key === 'lighthouse.remote_allow_list' ? Object.keys(o.value) : []
  );
  const localAllowNames = existingOverrides.flatMap((o) => {
    if (o.key === 'lighthouse.local_allow_list') {
      const { interfaces, ...cidrs } = o.value;
      return [...Object.keys(cidrs), ...Object.keys(interfaces)];
    }
    return [];
  });

  return (
    <Modal isOpen={true} onClose={onCancel} ref={modalRef}>
      <ModalHeader>Edit config</ModalHeader>
      <ModalBody>
        <Suspense fallback={<LoadingSpinner />}>
          <EditConfigOverrideFormSwitch
            configType={configType}
            onSave={onSave}
            value={value}
            stats={stats}
            remoteCIDRs={remoteCIDRs}
            localAllowNames={localAllowNames}
          />
        </Suspense>
      </ModalBody>
      <ModalFooter>
        <ConfirmButtonGroup
          variant="right"
          primaryButton={
            <Button type="submit" form={`${configType}-form`}>
              Save
            </Button>
          }
          secondaryButton={
            <Button variant="link" onClick={onCancel}>
              Cancel
            </Button>
          }
        />
      </ModalFooter>
    </Modal>
  );
}
